import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, Theme, Tooltip, useTheme, alpha } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  IFlightPairingDetails,
  IFullFlight,
  IFullPairingPosition,
  IPartialAPISParty,
  IPartialAPISPartyOption,
  IRider,
  IUser,
  sortCrewByDisplayOrder,
} from "@vmosolutions/oc-interfaces";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import CrewScheduleEventSummaryList, {
  ISummaryItem,
  ISummarySubHeader,
} from "src/components/CrewSchedule/CrewScheduleEventSummaryList";
import { PositionBadge } from "src/components/CrewSchedule/PositionBadge";
import { ShortCodeChip } from "src/components/ShortCodes/ShortCodeChip";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ManageRiders } from "./ManageRiders";
export interface IDisplayCrewRidersOnFlightProps {
  windowId: string;
  flight?: IFullFlight;
  isAPISCustomer: boolean;
  updateTabState?: (dirtyStatus: boolean) => void; // update parent with dirty status.
  crewOnFlight: Record<string, IFlightPairingDetails[]>;
  setLoadedFlight?: (flightValues: IFullFlight) => void;
}

enum CrewType {
  Crew = "crew",
  PosCrew = "posCrew",
  Rider = "rider",
}
//TODO move this interface to the interfaces folder if used elsewhere.
// New interfaces used only in the parsing of the crew on flight data.
export interface ICrewOnFlight {
  type: CrewType;
  position?: IFullPairingPosition;
  crew?: IUser;
  pairingShort: string;
  pairingId: string;
}
export interface IPairingCrewInfo {
  pairingShort: string;
  pairingId: string;
  crew: IFullPairingPosition[];
  positioningCrew: IFullPairingPosition[];
}
interface IPairingCrewMap {
  [key: string]: IPairingCrewInfo;
}

const riderFormStyles = (theme: Theme): object => ({
  "& .pairing_info_accordion": {
    "&::before": { display: "none" },
    "&.Mui-expanded": { margin: 0 },
    color: theme.typography.body1.color,
    background: theme.custom.timeline.contextListBackground,
    boxShadow: "none",
    "& .pairing_info_summary": {
      minHeight: 30,
      "& .MuiAccordionSummary-content": {
        margin: "6px 0px",
      },
      "& .MuiAccordionSummary-expandIcon": {
        padding: "6px",
      },
      "& .crew_position_display": {
        color: theme.typography.body2.color,
        backgroundColor: "blue",
      },
    },
  },

  "& .form_header": {
    display: "flex",
    justifyContent: "space-between",
    margin: "0px 5px",
    "& .title": { margin: "0px 10px" },
  },

  "& .form_body": {
    "& .expansion_panel": {
      borderRadius: 0,
      backgroundColor: "transparent",
      boxShadow: "none",
      "&:before": { display: "none" },

      "&.Mui-expanded": {
        margin: "5px",
        border: `1px solid ${alpha(theme.custom.forms.inputText, 0.1)}`,
        "& .expansion_summary": {
          backgroundColor: alpha(theme.custom.forms.inputText, 0.1),
        },
      },

      "& .expansion_focused": {
        backgroundColor: "transparent",
      },

      "& .expansion_summary": {
        padding: 0,
        minHeight: "auto",
        justifyContent: "flex-start",
        cursor: "default",
        "& .MuiAccordionSummary-content": {
          margin: 0,
          flexGrow: 1,
          alignItems: "center",
        },
        "& .edit_mode_button, & .create_mode_button": {
          padding: 0,
          margin: "2px 5px",
          border: `1px solid ${theme.custom.forms.pristineColor}`,
          borderRadius: "3px",
          width: 22,
          height: 22,
          "&.Mui-disabled": { opacity: 0.5 },
          "& svg": {
            color: theme.custom.forms.pristineColor,
            width: 18,
            height: 18,
          },
          "&:hover": {
            border: `1px solid ${theme.custom.forms.focusedColor}`,
            "& svg": { color: theme.custom.forms.focusedColor },
          },
        },
      },
      "& .expansion_details": {
        padding: 0,
        flexDirection: "column",
      },
    },
  },
  "& .form_footer": {
    marginTop: "10px",
    display: "flex",
    justifyContent: "flex-start",
    "& button": { margin: "5px 5px" },
    "& .btn": {
      flexWrap: "wrap",
      display: "flex",
      height: 27,
      fontSize: 10,
      lineHeight: "12px",
      padding: "2px 8px",
      margin: "0px 4px",
      "&.submit": {
        color: theme.custom.forms.inputBack,
      },
    },
  },
  "& .dialog_spinner_progress": { margin: "-10px" },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& p.required_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
    padding: "10px",
    width: "100%",
  },
  "& .optional_fields": {
    margin: "2px 0px",
    padding: "10px",
  },
  // STYLE FIELDS (includes individual field settings)
  "& .field": {
    width: "auto",
    minWidth: 238,
    "&.textfield": {
      "&.revision": {
        width: 260,
        marginRight: "15px",
        "& .input_control": {
          "& label": { width: 150 },
          "& .input-base": { width: 80 },
        },
      },
      "&.multiline_short": {
        width: 350,
        "& .input_control": {
          "& label": { width: "auto" },
        },
      },
      "&.multiline_long": {
        width: "100%",
        maxWidth: 540,
        "& .input_control": {
          "& label": { width: 150 },
        },
      },
      "&.address": {
        width: 238,
        maxWidth: 540,
        "& .input_control": {},
      },
    },
    "&.selectfield": {
      "&.aircraft_type": { minWidth: 300 },
    },
  },
  "& .grid_item_divider": {
    marginLeft: "20px",
    borderLeft: `1px solid ${theme.custom.forms.labels}`,
  },
});

const summaryStyles = (theme: Theme): object => ({
  width: "100%",
  "& .title": {
    "& .MuiListItemText-root": {
      fontWeight: "initial",
    },
  },
  "& .manage_rider": {
    "& .form_container": {
      "& .select_rider": {
        width: "100%",
        margin: "0px 50px 0px 0px",
        "& .riders": {
          maxWidth: 250,
        },
      },
      "& .riders_accordion": {
        width: "100%",
        margin: "0px 5px",
        "& .field": {
          width: "100%",
          maxWidth: "400px",
        },
      },
    },
  },
});

const missingAPISStyles = (theme: Theme): object => ({
  width: "0.75em",
  height: "0.75em",
  marginLeft: "5px",
  color: theme.palette.warning.main,
});

const convertToCrewOnFlight = (item: IFlightPairingDetails, type: CrewType): ICrewOnFlight => {
  return {
    type: type,
    position: item.position,
    crew: item.position?.crew ?? undefined,
    pairingShort: item.pairingShort,
    pairingId: item.pairingId,
  };
};

const combineCrewArrays = (response: Record<string, IFlightPairingDetails[]>): ICrewOnFlight[] => {
  const { crew, positioningCrew } = response;
  const aircraftCrew =
    crew?.map((crew: IFlightPairingDetails) => ({ ...convertToCrewOnFlight(crew, CrewType.Crew) })) ?? [];
  const deadheads =
    positioningCrew?.map((dh: IFlightPairingDetails) => ({ ...convertToCrewOnFlight(dh, CrewType.PosCrew) })) ?? [];
  return [...aircraftCrew, ...deadheads];
};

/**
 * sort the pairings based on the crew's on pairing display order values
 * @param a - positions on pairing A
 * @param b - postions on pairing B
 * @returns
 */
const sortPairingsByPositionDisplayOrder = (a: IPairingCrewInfo, b: IPairingCrewInfo): number => {
  //I want the pairing to display first that has the lowest value of crew
  // add 1000 to pairings crew display ordering values if they are positioning crew
  const val =
    (a.crew.length > 0
      ? _.sumBy(a.crew, "position.displayOrder") / Math.max(a.crew.length, 1)
      : (_.sumBy(a.positioningCrew, "position.displayOrder") + 1000) / Math.max(a.positioningCrew.length, 1)) -
    (b.crew.length > 0
      ? _.sumBy(b.crew, "position.displayOrder") / Math.max(b.crew.length, 1)
      : (_.sumBy(b.positioningCrew, "position.displayOrder") + 1000) / Math.max(b.positioningCrew.length, 1));
  return val;
};
const mapCrewOnPairings = (data: ICrewOnFlight[]): IPairingCrewMap => {
  if (!data || data.length === 0) return null;
  const pairingCrewMap = data.reduce((acc: { [key: string]: any }, currentValue) => {
    // Place all crew in their respective pairings.
    const key = currentValue.pairingShort;
    const type = currentValue.type;
    const crewMember: IFullPairingPosition = {
      position: currentValue.position?.position,
      crew: currentValue.crew,
    };

    if (!currentValue.position) {
      acc[key] = {
        pairingShort: currentValue.pairingShort,
        pairingId: currentValue.pairingId,
        //? You could possibly pass empty crew slots based on aircraft config
        crew: [],
        positioningCrew: [],
      };
    } else if (acc[key]?.crew && type === "crew") {
      // add to existing pairing crew
      acc[key].crew.push(crewMember);
    } else if (acc[key]?.positioningCrew && type === "posCrew") {
      // add to existing pairing positioning crew
      acc[key].positioningCrew.push(crewMember);
    } else {
      // crew new pairing object.
      acc[key] = {
        pairingShort: currentValue.pairingShort,
        pairingId: currentValue.pairingId,
        crew: type === "crew" ? [crewMember] : [],
        positioningCrew: type === "posCrew" ? [crewMember] : [],
      };
    }
    return acc;
  }, {});
  // Iterate through all crew types on pairings and sort them by position display order.
  for (const key in pairingCrewMap) {
    pairingCrewMap[key].crew?.sort(sortCrewByDisplayOrder);
    pairingCrewMap[key].positioningCrew?.sort(
      (a: IFullPairingPosition, b: IFullPairingPosition) => a.position.displayOrder - b.position.displayOrder
    );
  }

  return pairingCrewMap;
};

/**
 * A wraper commponet to display the componet to add a comment to a flight  &
 * display all the comments currently on a flight
 * @param props - flight: IFullEvent
 */

export const DisplayCrewRidersOnFlight = (props: IDisplayCrewRidersOnFlightProps): JSX.Element => {
  const theme: Theme = useTheme();
  const { windowId, flight, isAPISCustomer, crewOnFlight, setLoadedFlight } = props;

  const [crewOnPairings, setCrewOnPairings] = useState<IPairingCrewMap>(null);

  const [ridersOnFlight, setRidersOnFlight] = useState<IRider[]>([]);
  const users = useSelector((state: IRootState) => state.structures.appUsers);
  // const crew = useMemo(() => users?.filter((user) => !!user.crewInfo), [users]);
  /////////////////////////
  // GET APIS PARTIES
  /////////////////////////
  const [APISParties, setAPISParties] = useState<IPartialAPISParty[]>([]);
  // const [selectedParty, setSelectedParty] = useState<IAPISParty>(null);
  useEffect(() => {
    const getAPISPartiesAction = async (): Promise<void> => {
      const response = await AmplifyAPIWrapper.Get<IPartialAPISParty[]>("/APIS/parties?omitMarkedForDeletion=false");
      if (Array.isArray(response)) setAPISParties(response);
    };
    if (isAPISCustomer) getAPISPartiesAction();
  }, [isAPISCustomer, setAPISParties]);

  const partialAPISPartyOptions: IPartialAPISPartyOption[] = useMemo(() => {
    return APISParties.map((party: IPartialAPISParty) => {
      return {
        ...party,
        employeeId: users.find((user) => user._id === party.user_id)?.employeeId,
      } as IPartialAPISPartyOption;
    });
  }, [APISParties, users]);

  useEffect(() => {
    if (flight) {
      setRidersOnFlight(flight.riders);
    }
  }, [flight, flight?.riders]);

  const pairingCrewInfo: IPairingCrewInfo[] = useMemo(() => {
    return crewOnPairings
      ? Object.values(crewOnPairings).sort((a: IPairingCrewInfo, b: IPairingCrewInfo) =>
          sortPairingsByPositionDisplayOrder(a, b)
        )
      : [];
  }, [crewOnPairings]);

  //get riders that are on a flight
  const ridersOnFlightAsAPISParty: IPartialAPISPartyOption[] = useMemo(() => {
    if (partialAPISPartyOptions.length && ridersOnFlight) {
      return ridersOnFlight.map((rider: IRider) => {
        const result = partialAPISPartyOptions.find(
          (party: IPartialAPISPartyOption) => party._id === rider.apisparty_id
        );
        if (result?.APISEmploymentCategory) result.APISEmploymentCategory = rider.APISEmploymentCategory;
        return result;
      });
    } else {
      return [];
    }
  }, [partialAPISPartyOptions, ridersOnFlight]);

  //check to see if the crew is in the APIS info
  const isAPISParty = useCallback(
    (crewUserId: string): boolean => {
      const exists = partialAPISPartyOptions?.find(
        (party: IPartialAPISPartyOption) => !party.markedForDeletion && party.user_id === crewUserId
      );
      return exists ? true : false;
    },
    [partialAPISPartyOptions]
  );

  useEffect(() => {
    if (crewOnFlight?.crew || crewOnFlight?.positioningCrew) {
      setCrewOnPairings(mapCrewOnPairings(combineCrewArrays(crewOnFlight)));
    }
  }, [crewOnFlight]);

  const summaryRidersItems: Array<ISummaryItem | ISummarySubHeader> = useMemo(
    () =>
      ridersOnFlightAsAPISParty
        .sort((a, b) => {
          const nameA: string = a?.lastName.toUpperCase(); // ignore upper and lowercase
          const nameB: string = b?.lastName.toUpperCase();
          return nameA < nameB ? -1 : nameA > nameB ? 1 : nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        })
        .map((rider: IPartialAPISPartyOption) => {
          // might be workable! (from Emily)
          // const foundCrew: IUser = crew?.find((c: IUser) => c._id === rider.user_id);
          return {
            key: rider?.markedForDeletion ? `markedForDeletion-${rider._id}` : `${rider?._id}`,
            value: `${rider?.lastName}, ${rider?.firstName} ${rider?.employeeId ? ` (${rider.employeeId})` : ""}`,
            label: `${rider?.APISEmploymentCategory}`,
          };
        }),
    [ridersOnFlightAsAPISParty]
  );

  return (
    <Grid sx={riderFormStyles(theme)} container direction={"column"}>
      {pairingCrewInfo.map((pairing) => {
        return (
          <Accordion
            className="pairing_info_accordion"
            key={"pairing_info_accordion" + pairing.pairingShort}
            defaultExpanded={true}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="pairing_info_summary">
              <Grid container alignItems="center">
                <Grid item>
                  <span style={{ fontSize: 16, fontWeight: 700, marginRight: 10 }}>Pairing</span>
                </Grid>
                <Grid item>
                  <ShortCodeChip code={pairing.pairingShort} itemId={pairing.pairingId} type="pairing" edit={true} />
                </Grid>
                <Grid item>
                  <span style={{ fontSize: 12, marginLeft: 10, color: "white" }}>
                    {`Crew (${pairing.crew.length}) Positioning Crew (${pairing.positioningCrew.length})`}
                  </span>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction="column">
                {pairing.crew?.length > 0 && (
                  <Grid item>
                    <span style={{ fontSize: 14, color: "white" }}>Crew</span>
                    {pairing.crew?.map((item, index) => {
                      const isAPISError = isAPISCustomer && !isAPISParty(item?.crew?._id);
                      return (
                        <Grid
                          key={index}
                          container
                          className="crew_position_display"
                          alignItems="flex-start"
                          wrap="nowrap"
                        >
                          <PositionBadge
                            position={item.position}
                            filled={!!item?.crew}
                            isInactive={!!item?.crew && !item?.crew?.crewInfo?.isActive}
                          />
                          <Grid
                            item
                            style={
                              isAPISError ? { marginLeft: 5, color: theme.palette.warning.main } : { marginLeft: 5 }
                            }
                          >
                            {item?.crew
                              ? `${item?.crew?.lastName}, ${item?.crew?.firstName} (${item?.crew?.employeeId})`
                              : "Unassigned"}
                          </Grid>

                          {isAPISError && (
                            <Tooltip title="Missing APIS Information" enterDelay={800} placement="top">
                              <div>
                                <ErrorIcon sx={missingAPISStyles(theme)} color="error" />
                              </div>
                            </Tooltip>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}

                {pairing.positioningCrew?.length > 0 && (
                  <Grid item>
                    <span style={{ fontSize: 14, color: "white" }}>Positioning Crew</span>
                    {pairing.positioningCrew.map((item, index) => {
                      const isAPISError = isAPISCustomer && !isAPISParty(item?.crew?._id);
                      return (
                        <Grid
                          key={index}
                          container
                          className="crew_position_display"
                          alignItems="flex-start"
                          wrap="nowrap"
                        >
                          <PositionBadge
                            position={item.position}
                            filled={true}
                            isInactive={!!item?.crew && !item?.crew?.crewInfo?.isActive}
                          />
                          <Grid
                            item
                            style={
                              isAPISError ? { marginLeft: 5, color: theme.palette.warning.main } : { marginLeft: 5 }
                            }
                          >
                            {item?.crew
                              ? `${item?.crew?.lastName}, ${item?.crew?.firstName} (${item?.crew?.employeeId})`
                              : "Unassigned"}
                          </Grid>
                          {isAPISError && (
                            <Tooltip title="Missing APIS Information" enterDelay={800} placement="top">
                              <ErrorIcon sx={missingAPISStyles(theme)} color="error" />
                            </Tooltip>
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {isAPISCustomer && (
        <Grid container wrap="nowrap" sx={summaryStyles(theme)}>
          <CrewScheduleEventSummaryList
            summaryTitle={`Riders  (${summaryRidersItems?.length})`}
            key={"summaryList"}
            items={summaryRidersItems}
            // hideTitle={true}
          />
        </Grid>
      )}

      {isAPISCustomer && (
        <Grid container wrap="nowrap" sx={summaryStyles(theme)}>
          <ManageRiders
            id={windowId}
            layout="vertical"
            ridersOnFlight={ridersOnFlight ?? []}
            riderOptions={partialAPISPartyOptions}
            updateRidersOnFlight={setRidersOnFlight}
            flightId={flight._id}
            setLoadedFlight={setLoadedFlight}
          />
        </Grid>
      )}
    </Grid>
  );
};
